import Vue from 'vue';
import localforage from 'localforage';

import { arrayToObjectWithIdsAsKeys, getWidgetItemType } from '@/helpers/utils';
import { cacheData } from '@/helpers/caching';
import {
  RESET_PLAYER,
  PLAYER_LOAD_LAYOUT,
  PLAYER_LOAD_LAYOUT_CHILDREN,
  PLAYER_LOAD_LAYOUT_ERROR,
  USE_PLAYER_CACHED_DATA,
  PLAYER_SET_LAYOUT,
  SET_LAYOUT_CHILDREN,
  SET_PLAYLIST_CHILDREN,
  SET_TEMPLATE_ITEMS,
  SET_PLAYLIST_TEMPLATE_ITEMS,
  SET_LOCAL_TEMPLATE_ITEMS,
  SET_APP_DATA,
  SET_PLAYER_ROTATION,
  PLAYER_SET_LAYOUT_ORIENTATION,
  OPEN_PLAYER_MODAL,
  CLOSE_PLAYER_MODAL,
  PLAYER_CLEAR_RETRY_INTERVAL,
  PLAYER_SETUP_HEALTH_CHECK,
  PLAYER_HANDLE_LOAD_ERROR,
  CHANGE_TEMPLATE_SLIDE_VISIBILITY,
  MUTATE_TEMPLATE_SLIDE_VISIBILITY,
} from '@/store/actions/player';
import { LAYOUT_RESOLUTIONS, WIDGET_TYPES } from '@/models/layoutDesigner';

import { apiGetScreenLayout, apiGetScreenLayoutChildren } from '@/api/layouts';
import { apiGetScreenSnapshot } from '@/api/snapshot';
import { extractSnapshotStoreSlices } from '@/helpers/snapshot';
import { logger } from '@/utils/screenEventsLogger';
import { PLAYER_ORIENTATION, SCREEN_ORIENTATION } from '@/constants';

// Retry configuration
const RETRY_INTERVAL = 3 * 60 * 1000;
const MAX_RETRIES = 40; // 2 hour total

const state = {
  loadingLayout: false,
  layout: null,
  widgets: {},
  playlistsItems: {},
  templateItems: {},
  templateOnPlaylistItems: {},
  localTemplatesItems: {},
  isPlayerHorizontal: false,
  screenId: null,
  showPlayerModal: false,
  modalContent: null,
  appsData: {},
  layoutLoadRetryCount: 0,
  layoutLoadRetryInterval: null,
  templateSlideVisibility: {},
};

const getters = {
  getPlayerRotation: (state) => (state.isPlayerHorizontal ? 'horizontal' : 'vertical'),

  getPlaylistItems: (state) => (assocId) => {
    const playlistItems = state.playlistsItems[assocId];

    if (!playlistItems) return null;

    const sortedPlaylistItems = Object.values(playlistItems).sort(
      (item1, item2) => item1.item_priority - item2.item_priority,
    );

    return sortedPlaylistItems;
  },

  getTemplateSlideVisibility: (state) => (assocId) => {
    return state.templateSlideVisibility[assocId];
  },
};

const actions = {
  [PLAYER_CLEAR_RETRY_INTERVAL]({ state }) {
    if (state.layoutLoadRetryInterval) {
      clearInterval(state.layoutLoadRetryInterval);
      state.layoutLoadRetryInterval = null;
    }
  },

  [PLAYER_SETUP_HEALTH_CHECK]({ state, dispatch }, { screenId }) {
    dispatch(PLAYER_CLEAR_RETRY_INTERVAL);

    state.layoutLoadRetryInterval = setInterval(() => {
      const needRefresh = !state.layout || Object.keys(state.widgets).length === 0;

      if (needRefresh) {
        if (state.layoutLoadRetryCount < MAX_RETRIES) {
          logger.addLog(
            `Attempt to reload layout (attempt ${state.layoutLoadRetryCount + 1}/${MAX_RETRIES})`,
          );
          state.layoutLoadRetryCount++;

          dispatch(PLAYER_LOAD_LAYOUT, { screenId, isRetry: true });
        } else {
          logger.addLog('ERROR: Max retry attempts reached for layout loading');
          dispatch(PLAYER_CLEAR_RETRY_INTERVAL);
        }
      }
    }, RETRY_INTERVAL);
  },

  [PLAYER_HANDLE_LOAD_ERROR]({ state, dispatch }, { error, screenId }) {
    if (state.layoutLoadRetryCount < MAX_RETRIES) {
      logger.addLog(
        `ERROR: Layout load failed. Message: ${error.message || 'NA'}. Will retry in ${
          RETRY_INTERVAL / 1000
        } seconds (attempt ${state.layoutLoadRetryCount + 1}/${MAX_RETRIES})`,
      );

      dispatch(PLAYER_CLEAR_RETRY_INTERVAL);

      state.layoutLoadRetryInterval = setTimeout(() => {
        state.layoutLoadRetryCount++;
        dispatch(PLAYER_LOAD_LAYOUT, { screenId, isRetry: true });
      }, RETRY_INTERVAL);
    } else {
      logger.addLog('ERROR: Max retry attempts reached for layout loading');
      logger.addLog('Max retry attempts reached for layout loading');
    }
  },

  [PLAYER_LOAD_LAYOUT]: async ({ commit, dispatch, state }, { screenId, isRetry = false }) => {
    try {
      logger.addLog(`Started loading layout and its children for screen ${screenId}`);
      dispatch(PLAYER_CLEAR_RETRY_INTERVAL);

      if (!isRetry) {
        commit(RESET_PLAYER, { screenId });
        state.layoutLoadRetryCount = 0;
      }

      commit(PLAYER_LOAD_LAYOUT);

      // Snapshot bundles layout + all its children (playlists/templates/tiles) in one call.
      // Response is null if the snapshot endpoint isn't available yet, or the request fails.
      const snapshotResponse = await apiGetScreenSnapshot(screenId);

      let layout;
      let snapshotChildren = null;
      let legacyResponse = null;

      if (snapshotResponse) {
        layout = await cacheData(`screen-${screenId}-layout`, snapshotResponse.data);
        snapshotChildren = layout?.children || null;
      } else {
        // As a fall back we will use the legacy per-endpoint flow (layout, then layout/children, then each
        // widget fetching its own children).
        legacyResponse = await apiGetScreenLayout(screenId);

        layout = legacyResponse
          ? await cacheData(`screen-${screenId}-layout`, legacyResponse.data)
          : await localforage.getItem(`screen-${screenId}-layout`);
      }

      if (!layout) throw new Error('Unable to load screen');

      commit(PLAYER_SET_LAYOUT, layout);
      commit(PLAYER_SET_LAYOUT_ORIENTATION, layout.settings.isHorizontal);

      // Forcing screen resolution on Screen
      const { aspectRatio, resolution } = layout.settings;
      const isHorizontal = state.isPlayerHorizontal;
      const { width, height } = LAYOUT_RESOLUTIONS.resolutions[aspectRatio][resolution];

      document.getElementById('viewport').setAttribute(
        'content',
        `user-scalable=no,
        height=${isHorizontal ? height : width},
        width=${isHorizontal ? width : height}`,
      );

      const isOnline = !!snapshotResponse || !!legacyResponse;

      await dispatch(!isOnline ? USE_PLAYER_CACHED_DATA : PLAYER_LOAD_LAYOUT_CHILDREN, {
        layout,
        screenId,
        snapshotChildren,
      });

      // Setup health check after successful load
      dispatch(PLAYER_SETUP_HEALTH_CHECK, { screenId });
      logger.addLog(`Finished loading layout and its children for screen ${screenId}`);
    } catch (error) {
      logger.addLog(
        `ERROR: loading layout and its children for screen ${screenId} - ${error.message || 'NA'}`,
      );
      commit(PLAYER_LOAD_LAYOUT_ERROR, error);
      dispatch(PLAYER_HANDLE_LOAD_ERROR, { error, screenId });
    }
  },

  async [USE_PLAYER_CACHED_DATA]({ commit, state, dispatch }, { layout, screenId }) {
    state.layout = layout;

    const widgets = await localforage.getItem(`layoutChildren-${screenId}`);

    if (widgets) {
      commit(SET_LAYOUT_CHILDREN, { layoutChildren: widgets, screenId, cache: false });
    } else {
      logger.addLog("No Screen's children found on cache");

      // Try to get children if not cached
      dispatch(PLAYER_LOAD_LAYOUT_CHILDREN, {
        layout,
        screenId,
      });
    }
  },

  [PLAYER_LOAD_LAYOUT_CHILDREN]: async ({ commit }, { screenId, snapshotChildren = null }) => {
    try {
      let widgets;

      if (snapshotChildren) {
        widgets = await cacheData(`layoutChildren-${screenId}`, snapshotChildren);
      } else {
        const response = await apiGetScreenLayoutChildren(screenId);

        widgets = response
          ? await cacheData(`layoutChildren-${screenId}`, response.data)
          : await localforage.getItem(`layoutChildren-${screenId}`);
      }

      if (!widgets) throw new Error('Unable to load screen');

      const formatedWidgets = widgets.map((widget) => {
        const isAnApp = widget.object.item_type ? widget.object.item_type.includes('app') : false;

        let itemType = getWidgetItemType(widget);

        if (isAnApp) {
          if (widget.object.config && widget.object.config.override) {
            delete widget.object.config.override;
          }

          if (widget.object.override && widget.object.override.config.override) {
            delete widget.object.override.config.override;
          }
        }

        let layouObject = isAnApp
          ? Object.assign({}, widget.object, widget.object.config)
          : widget.object;

        if (widget.object.override) {
          if (
            itemType === WIDGET_TYPES.TEXT ||
            itemType === WIDGET_TYPES.LAYER ||
            itemType === WIDGET_TYPES.RICH_TEXT ||
            (itemType === WIDGET_TYPES.APP && layouObject.item_type.includes('table'))
          ) {
            layouObject = { ...layouObject, ...layouObject.override.config };
          }
        }

        return Object.assign({}, widget, {
          assoc_id: widget.assoc_id,
          object: Object.assign({}, layouObject, { type: itemType }),
          itemType,
        });
      });

      let fileCount = 0;
      const files = {};

      formatedWidgets.forEach((widget) => {
        if (widget.object.type === 'video') {
          fileCount++;
          files[widget.object.item_id] = widget.position.data.url;
        }
      });

      commit(SET_LAYOUT_CHILDREN, {
        layoutChildren: arrayToObjectWithIdsAsKeys(formatedWidgets, 'assoc_id'),
        screenId,
      });

      if (snapshotChildren) {
        const {
          playlistsItems,
          templateItems,
          templateOnPlaylistItems,
          localTemplatesItems,
          cacheWrites,
        } = extractSnapshotStoreSlices(formatedWidgets);

        Object.entries(playlistsItems).forEach(([assocId, playlistItems]) =>
          commit(SET_PLAYLIST_CHILDREN, { assocId, playlistItems }),
        );
        Object.entries(templateItems).forEach(([assocId, templateWidgets]) =>
          commit(SET_TEMPLATE_ITEMS, { key: assocId, widgets: templateWidgets }),
        );
        Object.entries(templateOnPlaylistItems).forEach(([playlistId, templateWidgets]) =>
          commit(SET_PLAYLIST_TEMPLATE_ITEMS, { key: playlistId, widgets: templateWidgets }),
        );
        Object.entries(localTemplatesItems).forEach(([playlistId, byTile]) =>
          Object.entries(byTile).forEach(([tileId, tileWidgets]) =>
            commit(SET_LOCAL_TEMPLATE_ITEMS, { playlistId, key: tileId, widgets: tileWidgets }),
          ),
        );
        cacheWrites.forEach(({ key, data }) => cacheData(key, data));
      }

      return formatedWidgets;
    } catch (error) {
      commit(PLAYER_LOAD_LAYOUT_ERROR, error);
      logger.addLog(
        `ERROR: loading layout children for screen ${screenId} - ${error.message || 'NA'}`,
      );
      return false;
    }
  },

  [CHANGE_TEMPLATE_SLIDE_VISIBILITY]: ({ commit }, { assocId, visibility }) => {
    commit(MUTATE_TEMPLATE_SLIDE_VISIBILITY, { assocId, visibility });
  },
};

const mutations = {
  [PLAYER_LOAD_LAYOUT]: (state) => {
    state.loadingLayout = true;
  },

  [PLAYER_SET_LAYOUT]: (state, payload) => {
    state.layout = payload;
  },

  [PLAYER_LOAD_LAYOUT_ERROR]: (state, error) => {
    state.loadingLayout = false;
  },

  [RESET_PLAYER]: (state, { screenId = null }) => {
    state.layout = null;
    state.widgets = {};
    state.playlistsItems = {};
    state.templateItems = {};
    state.templateOnPlaylistItems = {};
    state.localTemplatesItems = {};
    state.appsData = {};

    state.screenId = screenId;
  },

  [SET_LAYOUT_CHILDREN]: (state, { layoutChildren, screenId, cache = true }) => {
    state.loadingLayout = false;

    if (cache) cacheData(`layoutChildren-${screenId}`, layoutChildren);

    Vue.set(state, 'widgets', layoutChildren);
  },

  [OPEN_PLAYER_MODAL]: (state, modalContent) => {
    state.showPlayerModal = true;
    state.modalContent = modalContent;
  },

  [CLOSE_PLAYER_MODAL]: (state) => {
    state.showPlayerModal = false;
    state.modalContent = null;
  },

  [SET_PLAYLIST_CHILDREN](state, { assocId, playlistItems = {} }) {
    Vue.set(state.playlistsItems, assocId, playlistItems);
  },

  [SET_TEMPLATE_ITEMS](state, { key, widgets }) {
    Vue.set(state.templateItems, key, widgets);
  },

  [SET_PLAYLIST_TEMPLATE_ITEMS](state, { key, widgets }) {
    Vue.set(state.templateOnPlaylistItems, key, widgets);
  },

  [SET_LOCAL_TEMPLATE_ITEMS](state, { playlistId, key, widgets }) {
    if (!state.localTemplatesItems[playlistId]) Vue.set(state.localTemplatesItems, playlistId, {});

    Vue.set(state.localTemplatesItems[playlistId], key, widgets);
  },

  [SET_APP_DATA](state, { appId, appData }) {
    Vue.set(state.appsData, appId, appData);
  },

  [SET_PLAYER_ROTATION]: (state, value = false) => {
    if (state.layout) {
      const layout = JSON.parse(JSON.stringify(state.layout));
      layout.settings.isHorizontal = value;
      Vue.set(state, 'layout', layout);
    }
    state.isPlayerHorizontal = value;
  },

  [PLAYER_SET_LAYOUT_ORIENTATION]: (state, value) => {
    // Orientation saved from screen events takes precedence, as layout settings can be wrong
    const storedOrientation = localStorage.getItem(PLAYER_ORIENTATION);
    const isHorizontal = Object.values(SCREEN_ORIENTATION).includes(storedOrientation)
      ? storedOrientation === SCREEN_ORIENTATION.LANDSCAPE ||
        storedOrientation === SCREEN_ORIENTATION.REVERSE_LANDSCAPE
      : value;

    if (state.layout && state.layout.settings) {
      state.layout.settings.isHorizontal = isHorizontal;
    }
    state.isPlayerHorizontal = isHorizontal;
  },

  [MUTATE_TEMPLATE_SLIDE_VISIBILITY]: (state, { assocId, visibility }) => {
    Vue.set(state.templateSlideVisibility, assocId, visibility);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
